import React, { useState, useEffect } from 'react';
import './SuperAdminCatalogManagement.css';
import '../ContentArchitect/ContentArchitect.css';
import CategoriesTab from './CategoriesTab';
import CatalogViewTab from './CatalogViewTab';
import FeaturedTab from './FeaturedTab';
import StructureTab from '../ContentArchitect/components/StructureTab';
import ApprovalQueue from '../ContentArchitect/components/ApprovalQueue';
import useSuperAdminCatalogManagementStore from '@/stores/SuperAdminStore/CatalogManagementStore';

type CatalogTab = 'categories' | 'catalog' | 'featured' | 'structure' | 'approval';

const CatalogManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CatalogTab>('categories');

  const {
    categories,
    catalogCourses,
    featuredCourses,
    recommendationRules,
    fetchCategories,
    fetchCatalogCourses,
    fetchFeaturedCourses,
    fetchRecommendationRules,
  } = useSuperAdminCatalogManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'categories' && !categories) {
      fetchCategories();
    }
  }, [activeTab, categories, fetchCategories]);

  useEffect(() => {
    if (activeTab === 'catalog' && !catalogCourses) {
      fetchCatalogCourses();
    }
  }, [activeTab, catalogCourses, fetchCatalogCourses]);

  useEffect(() => {
    if (activeTab === 'featured' && !featuredCourses) {
      fetchFeaturedCourses();
    }
    if (activeTab === 'featured' && !recommendationRules) {
      fetchRecommendationRules();
    }
  }, [activeTab, featuredCourses, recommendationRules, fetchFeaturedCourses, fetchRecommendationRules]);

  return (
    <div className="s-admin-catalog-management">
      <div className="s-admin-catalog-breadcrumb">
        <span className="s-admin-catalog-breadcrumb-item">Content Management</span>
        <span className="s-admin-catalog-breadcrumb-separator">&gt;</span>
        <span className="s-admin-catalog-breadcrumb-item s-admin-catalog-current">Catalog</span>
      </div>

      <header className="s-admin-catalog-page-header">
        <div className="s-admin-catalog-header-content">
          <h1 className="s-admin-catalog-page-title">Catalog</h1>
          <p className="s-admin-catalog-page-subtitle">Manage taxonomy, course discoverability, structure, and publishing</p>
        </div>
      </header>

      <nav className="s-admin-catalog-tabs">
        <div className="s-admin-catalog-tab-group">
          <span className="s-admin-catalog-tab-group-label">Catalog</span>
          <button
            className={`s-admin-catalog-tab ${activeTab === 'categories' ? 's-admin-catalog-active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`s-admin-catalog-tab ${activeTab === 'catalog' ? 's-admin-catalog-active' : ''}`}
            onClick={() => setActiveTab('catalog')}
          >
            Catalog View
          </button>
          <button
            className={`s-admin-catalog-tab ${activeTab === 'featured' ? 's-admin-catalog-active' : ''}`}
            onClick={() => setActiveTab('featured')}
          >
            Featured & Recommended
          </button>
        </div>

        <div className="s-admin-catalog-tab-group">
          <span className="s-admin-catalog-tab-group-label">Governance</span>
          <button
            className={`s-admin-catalog-tab ${activeTab === 'structure' ? 's-admin-catalog-active' : ''}`}
            onClick={() => setActiveTab('structure')}
          >
            Course Structure
          </button>
          <button
            className={`s-admin-catalog-tab ${activeTab === 'approval' ? 's-admin-catalog-active' : ''}`}
            onClick={() => setActiveTab('approval')}
          >
            Approval Queue
          </button>
        </div>
      </nav>

      <div className="s-admin-catalog-content-section">
        {activeTab === 'categories' && <CategoriesTab />}
        {activeTab === 'catalog' && <CatalogViewTab />}
        {activeTab === 'featured' && <FeaturedTab />}
        {activeTab === 'structure' && <StructureTab />}
        {activeTab === 'approval' && <ApprovalQueue />}
      </div>
    </div>
  );
};

export default CatalogManagement;
